import { useCallback, useEffect, useRef, useState } from "react";
import { useBuildingStore } from "@/hooks/useBuildingStore";
import {
  BuildingLookupError,
  type BuildingLookupFailureCode,
} from "@/stores/buildings/lookupError";

type Section = "technical" | "rooms" | "certifications" | "permits" | "esg" | "lca";
type Attempt = { key: string; generation: number };
type LookupResult = Attempt & { complete: boolean; failure: BuildingLookupFailureCode | null };

function hasCompletedCurrentLookup(result: LookupResult | null, attempt: Attempt, key: string) {
  return (
    result?.key === key &&
    attempt.key === key &&
    result.generation === attempt.generation &&
    result.complete
  );
}

function readBuildingState(store: ReturnType<typeof useBuildingStore>, id?: string) {
  if (!id) return { detail: undefined, isLoading: false, error: null };
  return {
    detail: store.getById(id),
    isLoading: store.loadingById.get(id) ?? false,
    error: store.errorById.get(id),
  };
}

export function useBuildingDetail(id?: string, options?: { sections?: Section[] }) {
  const store = useBuildingStore();
  const requestKey = JSON.stringify([id, options?.sections ?? []]);
  const attempt = useRef({ key: requestKey, generation: 0 });
  const [activeAttempt, setActiveAttempt] = useState<Attempt>({ key: requestKey, generation: 0 });
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);

  const loadCurrent = useCallback(() => {
    if (!id) return Promise.resolve();
    const generation = attempt.current.generation + 1;
    const nextAttempt = { key: requestKey, generation };
    attempt.current = nextAttempt;
    setActiveAttempt(nextAttempt);
    setLookupResult({ key: requestKey, generation, complete: false, failure: null });
    const request = store.loadDetail(id);
    void request.then(
      () => {
        if (attempt.current.generation === generation && attempt.current.key === requestKey) {
          setLookupResult({ key: requestKey, generation, complete: true, failure: null });
        }
      },
      (error: unknown) => {
        if (attempt.current.generation === generation && attempt.current.key === requestKey) {
          setLookupResult({
            key: requestKey,
            generation,
            complete: true,
            failure: error instanceof BuildingLookupError ? error.code : "unexpected",
          });
        }
      }
    );
    return request;
  }, [id, requestKey, store]);

  useEffect(() => {
    void loadCurrent();
    return () => {
      attempt.current = { ...attempt.current, generation: attempt.current.generation + 1 };
    };
  }, [loadCurrent]);

  const isCurrentLookupComplete = hasCompletedCurrentLookup(
    lookupResult,
    activeAttempt,
    requestKey
  );

  return {
    ...readBuildingState(store, id),
    currentLookupFailure: isCurrentLookupComplete ? lookupResult.failure : null,
    isCurrentLookupComplete,
    refetch: loadCurrent,
  } as const;
}
